import { EQuizStatus } from '@constants';
import { Popover } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { SelectorWrapper, StyledTag, TextWrapper, Wrapper } from './QuizStatusBadge.styles';

type Props = {
  value?: EQuizStatus;
  isEditable?: boolean;
  onChange?: (status: EQuizStatus) => void;
};

export const QuizStatusBadge: React.FC<Props> = ({ value, isEditable, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('ManagerQuizzesTranslations');
  const { colors } = useTheme();

  const determineStyle = useCallback(() => {
    switch (value) {
      case EQuizStatus.PUBLISHED:
        return { color: colors.green6, backgroundColor: colors.green1, borderColor: colors.green4 };
      case EQuizStatus.DRAFT:
        return { color: undefined, backgroundColor: undefined, borderColor: undefined };
    }
    return { color: undefined, backgroundColor: undefined, borderColor: undefined };
  }, [value, colors]);

  const { color, backgroundColor, borderColor } = useMemo(determineStyle, [determineStyle]);

  if (!value) return null;

  const handleOpen = (o: boolean) => {
    if (!isEditable) return;
    setIsOpen(o);
  };

  const handleOnChange = (s: EQuizStatus) => {
    if (!isEditable) return;
    onChange?.(s);
    setIsOpen(false);
  };

  const content = (
    <SelectorWrapper>
      {Object.values(EQuizStatus).map(status => (
        <StyledTag
          key={status}
          $color={backgroundColor as string}
          $borderColor={borderColor as string}
          onClick={() => handleOnChange(status)}>
          <TextWrapper $color={color as string}>{t(status)}</TextWrapper>
        </StyledTag>
      ))}
    </SelectorWrapper>
  );

  return (
    <Wrapper>
      <Popover
        open={isOpen}
        content={content}
        placement="bottom"
        trigger={['click']}
        onOpenChange={handleOpen}>
        <StyledTag $color={backgroundColor as string} $borderColor={borderColor as string}>
          <TextWrapper $color={color as string}>{t(value)}</TextWrapper>
        </StyledTag>
      </Popover>
    </Wrapper>
  );
};
